import { Box, Stack, Typography, ButtonBase, Button } from '@mui/material';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import { useRef, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { MAX_IMAGE_DIMENSION, validateUploadFile } from '../../utils/fileValidation';

function ButtonMedium({ text, variant, color, href, to, component, ...props }) {
    const resolvedComponent = to ? RouterLink : href ? "a" : component;    
    return (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap'}}>
            <ButtonBase
                variant={variant}
                color={color}
                component={resolvedComponent}
                to={to}
                href={href}
                {...props}
                sx={{
                    backgroundColor: 'var(--color-text-main)',
                    color: 'var(--color-text-card)',
                    borderRadius: '5px',
                    textAlign: 'center',
                    textTransform: 'uppercase',                    
                }}
            >
                {text}
            </ButtonBase>
        </Box>
    );
}



function ButtonMediumFull({ text, variant, color, href, to, component, children, ...props }) {
    const resolvedComponent = to ? RouterLink : href ? "a" : component;    

    return (
        <Box sx={{ display: 'flex', gap: 2}}>
            <ButtonBase
                variant={variant}
                color={color}
                component={resolvedComponent}
                to={to}
                href={href}
                {...props}
                sx={{
                    backgroundColor: 'var(--color-text-main)',
                    color: 'var(--color-text-card)',
                    padding: '5px 10px',
                    borderRadius: '5px',
                    textAlign: 'center',
                    textTransform: 'uppercase',
                    width: '100%',
                    boxSizing: 'border-box',
                    height: '40px',       
                }}
            >
                {children && (
                    <span
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        {children}
                    </span>
                )}
                <span>{text}</span> 
            </ButtonBase>
        </Box>
    );
}

function ButtonMediumFullGreen({ text, variant, color, href, to, component, children, ...props }) {
    const resolvedComponent = to ? RouterLink : href ? "a" : component;    

    return (
        <Box sx={{ display: 'flex', gap: 2, width: "100%" }}>
            <ButtonBase
                variant={variant}
                color={color}
                component={resolvedComponent}
                to={to}
                href={href}
                {...props}
                sx={{
                    backgroundColor: 'var(--green-color)',
                    color: 'var(--color-text-card)',
                    padding: '5px 10px',
                    borderRadius: '5px',
                    textAlign: 'center',
                    textTransform: 'uppercase',
                    width: '100%',
                    boxSizing: 'border-box',
                    height: '40px',       
                }}
            >
                {children && (
                    <span
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        {children}
                    </span>
                )}
                <span>{text}</span> 
            </ButtonBase>
        </Box>
    );
}

function ButtonText({ text, variant, color, href, to, component, ...props }) {
    const resolvedComponent = to ? RouterLink : href ? "a" : component;    

    return (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', padding: '5px' }}>
            <ButtonBase
                variant={variant}
                color={color}
                component={resolvedComponent}
                to={to}
                href={href}
                {...props}
                sx={{
                    backgroundColor: 'transparent',
                    color: 'var(--color-text-main)',
                    padding: '5px 10px',
                    borderRadius: '5px',
                    textAlign: 'center',
                    textTransform: 'uppercase',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1.5px 1.5px 0 #000',
                }}
            >
                {text}
            </ButtonBase>
        </Box>
    );
}

function ButtonSmall({ text, variant, color, href, to, sx = {}, component, children, ...props }) {
    const resolvedComponent = to ? RouterLink : href ? "a" : component;    

    return (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center',width: '100%' }}>
            <ButtonBase
                variant={variant}
                color={color}
                component={resolvedComponent}
                to={to}
                href={href}
                {...props}
                sx={{
                    backgroundColor: 'var(--color-text-main)',
                    color: 'var(--color-text-card)',
                    padding: '0px 10px',
                    borderRadius: '5px',
                    textAlign: 'center',
                    textTransform: 'uppercase', 
                    fontSize: '12px',
                    height: '23px',     
                    width: '100%',
                    ...sx,   
                }}
                size="small"
            >
                {children && (
                    <span
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        {children}
                    </span>
                )}
                <span>{text}</span> 
            </ButtonBase>
        </Box>
    );
}

function ButtonTextSmall({ text, variant, color, href, to, component, sx = {}, ...props }) {
    const resolvedComponent = to ? RouterLink : href ? "a" : component;

    return (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', padding: '3px' }}>
            <ButtonBase
                variant={variant}
                color={color}
                component={resolvedComponent}
                to={to}
                href={href}
                {...props}
                size="small"
                sx={{
                    backgroundColor: 'var(--color-text-main)',
                    color: 'var(--color-text-card)',
                    textAlign: 'center',
                    fontSize: '10px',
                    padding: '1px 2px',
                    minWidth: '40px',
                    borderRadius: '4px',
                    textTransform: 'none',
                    ...sx, // 👈 добавлено: позволяет переопределить внешний стиль
                }}
            >
                {text}
            </ButtonBase>
        </Box>
    );
}

function IconOnlyButton({
        icon,           
        size = "medium", 
        color = "primary", 
        href,           
        to,             
        component,      
        iconSize = 24,  
        ...props        
    }) {
        const resolvedComponent = to ? RouterLink : href ? "a" : component;

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
            }}
        >
            <ButtonBase
                variant="contained"
                color={color}
                component={resolvedComponent}
                to={to}
                href={href}
                {...props}
                size={size}
                className="iconOnlyButton"
                disableRipple={false}
                sx={{
                    minWidth: 0,      
                    padding: "1px",   
                    borderRadius: "50%", 
                    overflow: "hidden", 
                    position: "relative", 
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        width: `${iconSize}px`,
                        height: `${iconSize}px`,
                    }}
                >
                    {icon}
                </Box>
            </ButtonBase>
        </Box>
    );
}

function FileUploadButton({
    label = 'Выбрать файл',
    icon = null,
    color = 'primary',
    accept = '*',
    name = 'file',
    required = false,
    sx = {},
    onChange = () => {},
    asTrigger = false,
    ...props
    }) {
    const [fileName, setFileName] = useState('');

    const handleChange = (e) => {
        const file = e.target.files[0];
        if (file) {
        setFileName(file.name);
        }
        onChange(e);
    };

    // Если используется как триггер для ImageCropper → обычная кнопка без input
    if (asTrigger) {
        return (
        <Button
            variant="outlined"
            startIcon={icon}
            color={color}
            sx={{
            width: '100%',
            color: 'var(--color-text-card)',
            borderColor: 'var(--braun-color)',
            '&:hover': { borderColor: 'var(--braun-color)' },
            ...sx,
            }}
            {...props}
        >
            {label}
        </Button>
        );
    }

    // Обычный режим (со своим input)
    return (
        <Button
        variant="outlined"
        component="label"
        startIcon={icon}
        color={color}
        sx={{
            width: '100%',
            color: 'var(--color-text-card)',
            borderColor: 'var(--braun-color)',
            '&:hover': { borderColor: 'var(--braun-color)' },
            ...sx,
        }}
        {...props}
        >
        {fileName || label}
        <input
            type="file"
            name={name}
            accept={accept}
            hidden
            onChange={handleChange}
        />
        </Button>
    );
}


function ButtonSmooth({ text, iconSrc, variant, color, href, to, component, ...props }) {
  const resolvedComponent = to ? RouterLink : href ? 'a' : component;

  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', padding: 0 }}>
      <Button
        variant={variant}
        color={color}
        startIcon={
          iconSrc ? (
            <Box
              component="img"
              src={iconSrc}
              alt=""
              sx={{
                width: { xs: 14, sm: 18, md: 22 }, // ← адаптивная иконка
                height: { xs: 14, sm: 18, md: 22 },
                display: 'block',
              }}
            />
          ) : null
        }
        component={resolvedComponent}
        to={to}
        href={href}
        {...props}
        sx={{
          backgroundColor: 'var(--color-text-main)',
          color: '#223131',
          borderRadius: '22px',
          textAlign: 'center',
          textTransform: 'uppercase',

          // адаптивные размеры кнопки
          px: { xs: 2, sm: 2.5, md: 3 },              // padding X
          py: { xs: 0.8, sm: 1, md: 1.25 },           // padding Y
          minHeight: { xs: 40, sm: 44, md: 48 },      // высота
          minWidth: { xs: 140, sm: 160, md: 200 },    // ширина
          fontSize: { xs: 12, sm: 14, md: 16 },       // размер текста
          fontWeight: 700,
          lineHeight: 'normal',
          letterSpacing: 1,
          textDecoration: 'none',

          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',

          '&:hover': {
            backgroundColor: 'var(--button-light-hover)',
          },
          '&:active': {
            backgroundColor: 'var(--button-light-active)',
            
          },
        }}
      >
        {text}
      </Button>
    </Box>
  );
}


function ButtonTransparant({ text, variant, color, href, to, component, sx = {}, ...props }) {
    const resolvedComponent = to ? RouterLink : href ? "a" : component;

    return (
        <ButtonBase
            variant={variant}
            color={color}
            component={resolvedComponent}
            to={to}
            href={href}
            {...props}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                backgroundColor: 'transparent',
                color: 'inherit', // ← теперь наследуется от AppBar
                padding: { xs: '4px 6px', sm: '3px 6px', md: '3px 6px', lg: '4px 8px' },
                borderRadius: '4px',
                textAlign: 'center',
                textTransform: 'none',
                fontSize: { xs: '14px', sm: '13px', md: '16px', lg:'18px' },
                fontWeight: 300,
                textDecoration: 'none',
                minWidth: 'auto',
                height: '100%',
                whiteSpace: 'nowrap',
                 /* ВАЖНО: цвет подчёркивания по умолчанию */
                textDecorationColor: 'var(--color-text-palebrown)',
                textUnderlineOffset: '3px',       // красиво
                textDecorationThickness: '2px',   // чуть толще
                
                '&:hover': {
                    backgroundColor: 'transparent',
                    textDecoration: 'underline',
                },
                ...sx,
            }}
        >
            {text}
        </ButtonBase>
    );
}

function ButtonSmoothRegister({ text, iconSrc, variant, color, href, to, component, sx = {},...props }) {
    const resolvedComponent = to ? RouterLink : href ? 'a' : component;

    return (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', padding: '5px' }}>
        <Button
            variant={variant}
            color={color}
            component={resolvedComponent}
            to={to}
            href={href}
            {...props}
            disableRipple
            sx={{
            backgroundColor: 'var(--color-text-main)',
            color: 'var(--color-text-paleblack)',
            padding: '5px 10px',
            borderRadius: '22px',
            textAlign: 'center',
            textTransform: 'none',
            px: 3,
            py: 1.25,
            minHeight: 43,
            minWidth: 200,
            fontSize: 16,
            fontWeight: 500,
            lineHeight: 1.1,
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',

            '&:hover': {backgroundColor: 'var(--green-color)', color: '#fff', },
            '&:active': {backgroundColor: 'var(--button-green-active)',      },
            // 👇 для экранов от 900 до 1200 (md до lg)
            '@media (min-width:900px) and (max-width:1200px)': {
                minWidth: 160,
                fontSize: 16,
                px: 1,
                minHeight: 40,
            },
            '@media (max-width:600px)': {
                minWidth: 160,
                fontSize: 14,
                px: 2.5,
                minHeight: 40,
            },
            ...sx, // ← добавляем поддержку кастомного sx
            }}
        >
            {text}
        </Button>
        </Box>
    );
}

function ButtonSmoothEnter({ text, iconSrc, iconSrcDark, useDarkIcons = false, variant, color, href, to, component, sx = {}, ...props }) {
    const resolvedComponent = to ? RouterLink : href ? 'a' : component;
   

    return (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', padding: '5px' }}>
        <Button
            variant={variant}
            color="inherit"
            startIcon={
            iconSrc ? (
                <Box
                component="img"
                src={iconSrc}
                alt=""
                sx={{
                width:  22,
                height: 22,
                display: 'block',
                // если нет чёрной версии иконки, можно использовать фильтр
                filter: useDarkIcons ? 'invert(1)' : 'none', // ← сделает белую иконку из чёрной
                '@media (min-width:900px) and (max-width:1200px)': {
                 width: 16,
                 height: 16,
            },
                }}
                />
            ) : null
            }
            component={resolvedComponent}
            to={to}
            href={href}
            {...props}
            disableRipple
            sx={{
            backgroundColor: 'transparent', // прозрачный фон
            color: 'inherit',  // белый текст
            border: '2px solid currentColor',
            padding: '5px 10px',
            borderRadius: '22px',
            textAlign: 'center',
            textTransform: 'none',
            px: 3,
            py: 1.25,
            minHeight: 43,
            minWidth: 124,
            fontSize: 16,
            fontWeight: 400,
            lineHeight: 1.1,
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
            '&:hover': {
                backgroundColor: 'var(--green-color)', 
                borderColor: 'RGBA(111, 148, 127, 1)',
            },
            '&:active': {backgroundColor: 'var(--button-green-active)', borderColor: 'var(--button-green-active)',},
                '@media (min-width:900px) and (max-width:1200px)': {
                minWidth: 90,
                fontSize: 16,
                px: 1,
                minHeight: 40,
            },
            '@media (max-width:600px)': {
                minWidth: 90,
                fontSize: 14,
                px: 2.5,
                minHeight: 40,
            },
            ...sx, // ← добавляем поддержку кастомного sx
            }}
        >
            {text}
        </Button>
        </Box>
    );
}
function ButtonSmoothSeeAll({ text, iconSrc, variant, color, href, to, component, sx = {}, ...props }) {
    const resolvedComponent = to ? RouterLink : href ? 'a' : component;

    return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
      <Button
        component={resolvedComponent}
        to={to}
        href={href}
        {...props}
        disableRipple
        sx={{
          backgroundColor: 'transparent',
          color: 'rgba(34, 49, 49, 0.72)',
          border: '2px solid rgba(34, 49, 49, 0.72)',
          padding: '5px 10px',
          borderRadius: '22px',
          textAlign: 'center',
          textTransform: 'none',
          px: 3,
          py: 1.25,
          minHeight: 43,
          minWidth: 124,
          fontSize: 16,
          fontWeight: 500,
          lineHeight: 1.1,
          textDecoration: 'none',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.3s ease',
          
          '&:hover': {
            backgroundColor: 'RGBA(111, 148, 127, 1)',
            borderColor: 'RGBA(111, 148, 127, 1)',
            color: '#fff',
          },
          '&:active': {backgroundColor: 'var(--button-green-active)', borderColor: 'var(--button-green-active)',},
          '@media (min-width:900px) and (max-width:1200px)': {
            minWidth: 90,
            fontSize: 16,
            px: 1,
            minHeight: 40,
          },
          '@media (max-width:600px)': {
            minWidth: 90,
            fontSize: 14,
            px: 2.5,
            minHeight: 40,
          },
          ...sx,
        }}
      >
        Смотреть все
      </Button>
    </Box>
  );
}



// Кнопка для добавления картинок для описания
function ButtonAddImage({ onClick, sx = {}, ...props }) {
    return (
        <ButtonBase
            onClick={onClick}
            {...props}
            sx={{
                width: 86,
                height: 86,
                aspectRatio: '1 / 1',
                flexShrink: 0,
                borderRadius: "50%",
                backgroundColor: "var(--green-color)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                '&:hover': {
                    backgroundColor: 'var(--button-green-hover)', // <-- две черты
                },
                '&:not(.Mui-disabled):active': {
                    backgroundColor: 'var(--button-green-active)', // <-- правильное имя и :active
                    transform: 'translateY(0.5px)',
                },
                ...sx,
            }}
        >
        <svg
            width="30"
            height="30"
            viewBox="0 0 30 30"
            xmlns="http://www.w3.org/2000/svg"
        >
            {/* Горизонтальная линия */}
            <line
                x1="0"
                y1="15"
                x2="30"
                y2="15"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
            />
            {/* Вертикальная линия */}
            <line
                x1="15"
                y1="0"
                x2="15"
                y2="30"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
            />
        </svg>
        </ButtonBase>
    );
}

// Узкая кнопка для карточки путешевствия
function TavelButton({
        text,
        children,
        iconSrc,
        variant = 'contained',
        appearance = "filled", // ✅ new: "filled" | "outline"
        href,
        to,
        component,
        bgColor = 'var(--grey-color)',
        hoverColor,
        activeColor,
        textColor = 'inherit',
        fullWidth = false,
        size = '',
        sx: sxProp,
        ...props
    }) {
    const resolvedComponent = to ? RouterLink : href ? 'a' : component;
    const normalizedChildren = (Array.isArray(children) ? children : [children]).filter((child) => {
        if (child === null || child === undefined || child === false) return false;
        if (typeof child === "string") return child.trim().length > 0;
        return true;
    });
    const hasVisibleChildren = normalizedChildren.length > 0;
    const normalizedButtonText = String(text ?? "").trim().toLocaleLowerCase("ru-RU");
    const modalActionRole =
        normalizedButtonText === "отмена" ||
        normalizedButtonText === "отменить" ||
        normalizedButtonText === "назад"
            ? "cancel"
            : normalizedButtonText.startsWith("сохранить") ||
            normalizedButtonText === "создать" ||
            normalizedButtonText === "скопировать" ||
            props.type === "submit"
                ? "save"
                : undefined;
    const hasFlexOverride = (() => {
        if (!sxProp) return false;
        const items = Array.isArray(sxProp) ? sxProp : [sxProp];
        return items.some((item) => item && (item.flex !== undefined || item.flexGrow !== undefined));
    })();
    const isFixedMobileButton = size === 'minidelete' || size === 'mini';

    const sizeStyles = {
        minidelete: { minWidth: 34, width: 34, fontSize: 16, px: 2,  py: 0.75, height: 34 },
        mini: { minWidth: 30, fontSize: 16, px: 2,  py: 0.75, height: 34 },
        small: { minWidth: 120, fontSize: 16, px: 2,  py: 0.75, height: 34 },
        mediumsmall:{ minWidth: 140, fontSize: 16, px: 3,  py: 1.25, height: 44 },
        medium:{ minWidth: 200, fontSize: 16, px: 3,  py: 1.25, height: 44 },
    };

    const baseSx = {
        textTransform: 'none',
        backgroundColor: bgColor,
        color: textColor,
        borderRadius: '22px',
        textAlign: 'center',
        fontWeight: 400,
        lineHeight: 1.2,
        letterSpacing: 0,
        textDecoration: 'none',

        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',

        ...(hasFlexOverride ? {} : { flex: '0 0 auto' }),

        ...(hoverColor && {
            '&:hover': { backgroundColor: hoverColor },
        }),

        ...(activeColor && {
            '&:active': { backgroundColor: activeColor },
        }),

        // сначала размер
        ...sizeStyles[size],

        // а fullWidth ОБЯЗАТЕЛЬНО после sizeStyles
        ...(fullWidth
            ? {
                width: '100%',
                minWidth: 0,
                maxWidth: 'none',
                flexBasis: 'auto',
            }
            : {
                maxWidth: '100%',
            }),
    };

    return (
        <Button
            variant={variant}
            color="inherit"
            disableRipple
            fullWidth={fullWidth}
            startIcon={
            iconSrc ? (
                <Box
                component="img"
                src={iconSrc}
                alt=""
                sx={{
                    width: 22,
                    height: 22,
                    display: 'block',
                }}
                />
            ) : null
            }
            component={resolvedComponent}
            to={to}
            href={href}
            data-travel-modal-action={modalActionRole}
            {...props}
            sx={[
            {
                position: 'relative',
                overflow: 'visible',
            },
            baseSx,
            sxProp,
            // последний уровень защиты
            fullWidth && {
                width: '100% !important',
                minWidth: '0 !important',
                maxWidth: 'none !important',
            },
            ]}
        >
            {hasVisibleChildren ? normalizedChildren : text}
        </Button>
    );
}

// Кнопка для возврата на главное меню
function ButtonCircleReturnMainPage({
    to = "/",
    title = "На главную",
    zIndex = 9999,
    position = "fixed",

    // позиционирование по брейкпоинтам
    topXs = "1%",
    leftXs = "2%",
    topLg = 8,
    leftLg = "calc(2% + 12px)",

    // возможность кастомизировать стили для обеих версий
    sxXs = {},
    sxSm = {},
    containerSx = {},
}) {
    const usesOffsets = position !== "static" && position !== "relative";
    const tooltipTitle = usesOffsets ? title : "";

    return (
        <Box
            sx={{
                position,
                ...(usesOffsets && {
                    top: { xs: topXs, lg: topLg },
                    left: { xs: leftXs, lg: leftLg },
                    zIndex,
                }),
                ...containerSx,
            }}
        >
            {/* XS (<600px): узкая «стрелка» */}
            <Tooltip title={tooltipTitle} arrow>
                <IconButton
                component={RouterLink}
                to={to}
                aria-label="Назад на главную"
                size="small"
                sx={{
                    display: { xs: "inline-flex", sm: "none" },
                    width: 26,
                    height: 42,
                    borderRadius: "8px",
                    justifyContent: "center",
                    alignItems: "center",
                    background: "rgba(255,255,255,0.5)",
                    border: "1px solid rgba(0,0,0,0.05)",
                    boxShadow: "0 4px 10px rgba(0,0,0,0.06)",
                    backdropFilter: "blur(6px)",
                    WebkitBackdropFilter: "blur(6px)",
                    transition: "all 0.2s ease",
                    "&:hover": {
                    background: "rgba(255,255,255,0.65)",
                    boxShadow: "0 6px 14px rgba(0,0,0,0.1)",
                    },
                    ...sxXs,
                }}
                >
                <ArrowBackIosNewRoundedIcon sx={{ fontSize: 20, ml: 0.2, color: "#1f2937" }} />
                </IconButton>
            </Tooltip>

            {/* SM+ (>=600px): круглая «стеклянная» */}
            <Tooltip title={tooltipTitle} arrow>
                <IconButton
                    component={RouterLink}
                    to={to}
                    aria-label="Назад на главную"
                    sx={{
                        display: { xs: "none", sm: "inline-flex" },
                        width: 46,
                        height: 46,
                        borderRadius: "50%",
                        background: "rgba(255,255,255,0.15)",
                        border: "1px solid rgba(255,255,255,0.4)",
                        backdropFilter: "blur(10px)",
                        WebkitBackdropFilter: "blur(10px)",
                        boxShadow: "0 0 10px rgba(111,148,127,0.5)",
                        transition: "all 0.3s ease",
                        "&:hover": {
                        boxShadow: "0 0 20px rgba(111,148,127,0.9), inset 0 0 5px rgba(255,255,255,0.3)",
                        background: "rgba(255,255,255,0.25)",
                        },
                        ...sxSm,
                    }}
                >
                    <ArrowBackIosNewRoundedIcon
                        sx={{
                        fontSize: 20,
                        color: "rgba(15,23,42,0.9)",
                        filter: "drop-shadow(0 0 4px rgba(111,148,127,0.6))",
                        }}
                    />
                </IconButton>
            </Tooltip>
        </Box>
    );
}

// Широкая кнопка для добавления точек в карточке
function PointButton({
        text,
        iconSrc,
        variant,
        href,
        to,
        component,
        bgColor = 'var(--grey-color)',
        hoverColor = 'var(--grey-hover-color)',
        textColor = 'var(--color-text-card)',
        sx,
        style,
        ...props
    }) {
    const resolvedComponent = to ? RouterLink : href ? 'a' : component;

    return (
        <Box sx={{ display: 'contents' }}>
            <Button
                variant={variant}
                color="inherit"
                disableRipple
                startIcon={
                iconSrc ? (
                    <Box
                        component="img"
                        src={iconSrc}
                        alt=""
                        sx={{ width: 22, height: 22, display: 'block' }}
                    />
                ) : null
                }
                component={resolvedComponent}
                to={to}
                href={href}
                {...props}
                sx={{
                    textTransform: 'none',
                    backgroundColor: bgColor,
                    color: textColor,
                    padding: '5px 10px',
                    borderRadius: '12px',
                    textAlign: 'center',
                    px: 3,
                    py: 1.25,
                    minHeight: 48,
                    minWidth: 250,
                    fontSize: 16,
                    fontWeight: 400,
                    lineHeight: 1.2,
                    letterSpacing: 0,
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover': {backgroundColor: hoverColor,},
                    '@media (max-width:600px)': {
                        minWidth: 160,
                        fontSize: 14,
                        px: 2.5,
                        minHeight: 44,
                    },
                    ...(sx || {}),
                }}
            >
                {text}
            </Button>
        </Box>
    );
}
// Кнопка для импорта файлов
function FileImgButton({
        label = '',
        icon = null,
        formatsHint = 'PNG, JPG, WEBP',
        sizeHint = 'до 25 МБ, 1200×800+',
        accept = 'image/*',
        multiple = false,
        onSelect,
        tileSize = 140,
        tileSizeSm = 140,
        bgColor = '',
        hoverColor = '',
        activeColor = '',
        textColor = 'inherit',
        helperColor = 'text.secondary',
        borderColor = '',
        disabled = false,
        maxImageDimension = MAX_IMAGE_DIMENSION,
        validationType = "image",
        allowDrop = false,
        onValidationError,
        direction = "column",
        hideLabelOnMobile = false,
        sx = {},
        ...props
    }) {
    const inputRef = useRef(null);

    const handleOpenPicker = useCallback(() => {
        if (disabled) return;
        inputRef.current?.click();
    }, [disabled]);

    const selectFiles = useCallback(async (incoming) => {
        if (disabled) return;
        const files = Array.from(incoming || []).slice(0, multiple ? undefined : 1);
        for (const file of files) {
            const validation = await validateUploadFile(file, validationType, maxImageDimension);
            if (!validation.ok) {
                if (typeof onValidationError === "function") {
                    onValidationError(validation.error, file);
                } else if (typeof window !== "undefined") {
                    window.alert(validation.error);
                }
                return;
            }
        }

        if (files.length && onSelect) onSelect(files);
    }, [disabled, multiple, maxImageDimension, validationType, onSelect, onValidationError]);

    const handleChange = useCallback(async (e) => {
        const input = e.currentTarget;
        try { await selectFiles(input.files); } finally { input.value = ''; }
    }, [selectFiles]);

    const handleDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
        event.dataTransfer.dropEffect = disabled ? "none" : "copy";
    };
    const handleDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        void selectFiles(event.dataTransfer.files);
    };

    const ariaLabel = useMemo(() => {
        const extras = [formatsHint, sizeHint].filter(Boolean).join(', ');
        const accessibleLabel = label || 'Выбрать файл';
        return extras ? `${accessibleLabel}. ${extras}` : accessibleLabel;
    }, [label, formatsHint, sizeHint]);
    const fileRequirementsHint = useMemo(
        () => [formatsHint, sizeHint].filter(Boolean).join(' · '),
        [formatsHint, sizeHint]
    );

    return (
        <>
        <input
            ref={inputRef}
            type="file"
            accept={accept}
            multiple={multiple}
            onChange={handleChange}
            style={{ display: 'none' }}
            tabIndex={-1}
        />

        <ButtonBase
            component="button"
            type="button" 
            onClick={handleOpenPicker}
            onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleOpenPicker();
            }
            }}
            disabled={disabled}
            focusRipple
            aria-label={ariaLabel}
            onDragOver={allowDrop ? handleDragOver : undefined}
            onDrop={allowDrop ? handleDrop : undefined}
            {...props}
            sx={{
                width: 130,
                height: 130,
                display: 'inline-flex',
                border: '1px dashed',
                borderRadius: "12px",
                borderColor: borderColor || 'rgba(111, 148, 127, 0.62)',
                overflow: 'hidden',
                position: 'relative',
                bgcolor: bgColor || 'rgba(255, 255, 255, 0.24)',
                color: textColor,
                transition: (theme) => theme.transitions.create(['background-color', 'transform'], { duration: 120 }),
                '&:hover': {
                    bgcolor: hoverColor || 'rgba(111, 148, 127, 0.10)',
                    borderColor: 'var(--green-color)',
                },
                '&:active': { bgcolor: activeColor, transform: 'translateY(0.5px)' },
                '&.Mui-disabled': { opacity: 0.5, pointerEvents: 'none' },

                // адаптив
                '@media (max-width:600px)': {
                    width: tileSizeSm,
                    height: tileSizeSm,
                },

                // пользовательские стили
                ...sx,
                ...(validationType === 'image' && {
                    '@media (max-width:1719.95px), (max-height:850px)': {
                        maxWidth: '100%',
                        '& [data-upload-content]': {
                            boxSizing: 'border-box',
                            minWidth: 0,
                            padding: '6px',
                            gap: '4px',
                            '& > :not(style) ~ :not(style)': { margin: 0 },
                        },
                        '& [data-upload-icon]': {
                            width: 32,
                            height: 32,
                            flexShrink: 0,
                            '& .MuiSvgIcon-root': { fontSize: 22 },
                        },
                        '& [data-upload-label]': { fontSize: 12, lineHeight: 1.2 },
                        '& [data-upload-hints]': { display: 'none' },
                        '& [data-upload-help]': { display: 'grid', width: 20, height: 20, top: 4, right: 4 },
                    },
                }),
            }}
        >
            <Stack
                data-upload-content
                direction={direction}
                alignItems="center"
                justifyContent="center"
                spacing={1}
                sx={{
                    width: '100%',
                    height: '100%',
                    p: 2,
                    textAlign: 'center',
                }}
            >
            <Box
                data-upload-icon
                aria-hidden
                    sx={{
                    display: 'grid',
                    placeItems: 'center',
                    width:  46,
                    height: 46,
                    borderRadius: '50%',
                    color: 'var(--green-color)',
                    backgroundColor: 'rgba(111, 148, 127, 0.14)',
                    '& .MuiSvgIcon-root': { fontSize: 28 },
                    '@media (max-width:600px)': { width: 40, height: 40 },
                }}
            >
                {icon}
            </Box>

            {fileRequirementsHint && (
                <Tooltip title={fileRequirementsHint} arrow placement="top">
                    <Box
                        data-upload-help
                        component="span"
                        aria-label="Требования к файлу"
                        onMouseDown={(event) => event.stopPropagation()}
                        onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                        }}
                        sx={{
                            position: 'absolute',
                            top: 6,
                            right: 6,
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            display: { xs: 'grid', lg: 'none' },
                            placeItems: 'center',
                            color: helperColor === 'text.secondary'
                                ? 'var(--green-color)'
                                : helperColor,
                            backgroundColor: 'rgba(255,255,255,0.86)',
                            '& .MuiSvgIcon-root': { fontSize: 19 },
                        }}
                    >
                        <HelpOutlineRoundedIcon />
                    </Box>
                </Tooltip>
            )}

            {label && (
                <Typography
                    data-upload-label
                    variant="body2"
                    sx={{
                        fontWeight: 500,
                        lineHeight: 1.2,
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        wordBreak: 'break-word',
                        ...(hideLabelOnMobile && {
                            '@media (max-width:600px)': { display: 'none' },
                        }),
                    }}
                    title={label}
                >
                    {label}
                </Typography>
            )}

            {(formatsHint || sizeHint) && (
                <Stack
                    data-upload-hints
                    spacing={0.25}
                    sx={{ display: { xs: 'none', lg: 'flex' }, minWidth: 0 }}
                >
                    {formatsHint && (
                        <Typography
                            variant="caption"
                            sx={{ color: helperColor, lineHeight: 1.2 }}
                        >
                            {formatsHint}
                        </Typography>
                    )}
                    {sizeHint && (
                        <Typography
                            variant="caption"
                            sx={{ color: helperColor, lineHeight: 1.2 }}
                        >
                            {sizeHint}
                        </Typography>
                    )}
                </Stack>
            )}

            </Stack>
        </ButtonBase>
        </>
    );
}

FileImgButton.propTypes = {
    label: PropTypes.string,
    icon: PropTypes.node,
    formatsHint: PropTypes.string,
    sizeHint: PropTypes.string,
    accept: PropTypes.string,
    multiple: PropTypes.bool,
    onSelect: PropTypes.func,
    tileSize: PropTypes.number,
    tileSizeSm: PropTypes.number,
    bgColor: PropTypes.string,
    hoverColor: PropTypes.string,
    activeColor: PropTypes.string,
    textColor: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    helperColor: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    borderColor: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    disabled: PropTypes.bool,
    maxImageDimension: PropTypes.number,
    validationType: PropTypes.oneOf(["image", "gpx", "file"]),
    allowDrop: PropTypes.bool,
    onValidationError: PropTypes.func,
    hideLabelOnMobile: PropTypes.bool,
    sx: PropTypes.object,
};

export default FileImgButton;

export {
    ButtonMedium,
    ButtonMediumFull,
    ButtonMediumFullGreen,
    ButtonSmall,
    ButtonText,
    ButtonTextSmall,
    FileUploadButton,
    IconOnlyButton,
    ButtonSmooth,
    ButtonTransparant,
    ButtonSmoothRegister,
    ButtonSmoothEnter,
    ButtonAddImage,
    TavelButton,
    PointButton,
    FileImgButton,
    ButtonSmoothSeeAll, 
    ButtonCircleReturnMainPage,
};
